// src/pages/InterviewHistoryPage.js
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardCard from "../components/DashboardCard"; // Assuming this is a generic card component
import { getInterviewSessions } from "../services/interviewAssistantService"; // Adjust path
import Loader from "../components/Loader"; // Assuming you have a Loader
import { Eye } from "lucide-react"; // Added Eye icon

const InterviewHistoryPage = () => {
  const navigate = useNavigate();
  const [interviewSessions, setInterviewSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 5,
    totalPages: 0,
    totalRecords: 0,
  });

  const fetchInterviewSessions = async (page = 1) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getInterviewSessions(page, pagination.pageSize);
      // Backend paginated endpoint returns:
      // { data: [...], pageNumber: X, pageSize: Y, totalPages: Z, totalRecords: W }
      setInterviewSessions(response.data || []);
      setPagination({
        pageNumber: response.pageNumber,
        pageSize: response.pageSize,
        totalPages: response.totalPages,
        totalRecords: response.totalRecords,
      });
    } catch (error) {
      console.error("Error fetching interview sessions:", error);
      setError(error.message || "Failed to load sessions.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInterviewSessions();
  }, []);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchInterviewSessions(newPage);
    }
  };

  const dateFormatter = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-white p-4 sm:p-8 flex justify-center items-center">
        <Loader message="Loading interview history..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-white p-4 sm:p-8 text-center">
        <h2 className="text-2xl text-red-400 mb-4">Error Loading History</h2>
        <p className="text-gray-400 mb-6">{error}</p>
        <button
          onClick={() => navigate("/dashboard")}
          className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded-lg border border-slate-600 transition-all duration-300 text-sm"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-white p-4 sm:p-8">
      <header className="mb-10 flex items-center justify-between">
        <h1 className="text-3xl sm:text-4xl font-bold">
          Interview Practice History
        </h1>
        <button
          onClick={() => navigate("/dashboard")} // Or your main app page
          className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded-lg border border-slate-600 transition-all duration-300 text-sm"
        >
          Back to Dashboard
        </button>
      </header>

      {interviewSessions.length === 0 ? (
        <DashboardCard>
          <p className="text-center text-gray-400 py-8">
            No interview history found. Start practicing to see your progress
            here!
          </p>
        </DashboardCard>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {interviewSessions.map(
              (
                session // Changed 'interview' to 'session' for clarity
              ) => (
                <DashboardCard
                  key={session.id} // session.id is the sessionId
                  className="hover:shadow-blue-500/30 flex flex-col justify-between" // Added flex for button positioning
                >
                  <div>
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3">
                      <div>
                        <h4 className="text-lg font-semibold text-blue-400">
                          {session.title || "General Interview Practice"}
                        </h4>
                        <p className="text-xs text-gray-500">
                          {/* Ensure session.createdAt is a valid date string or timestamp */}
                          {session.createdAt
                            ? dateFormatter.format(new Date(session.createdAt))
                            : "Date N/A"}
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-400 mb-1">
                      CV:{" "}
                      <span className="font-mono text-gray-300 break-all">
                        {session.cvName || "N/A"}
                      </span>
                    </p>
                    <p className="text-sm text-gray-400 mb-3">
                      Questions Answered: {session.questionAnsweredCount ?? "N/A"}
                    </p>
                    {session.overallFeedbackSummary && (
                      <details className="bg-slate-700/30 p-3 rounded-md text-sm mb-4">
                        <summary className="cursor-pointer font-medium text-sky-400 hover:text-sky-300">
                          Overall Feedback Summary
                        </summary>
                        <p className="mt-2 text-gray-300 whitespace-pre-line">
                          {session.overallFeedbackSummary}
                        </p>
                      </details>
                    )}
                  </div>

                  <button
                    onClick={() => navigate(`/interview/history/${session.id}`)} // Navigate to detail page
                    className="w-full mt-auto px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-lg transition-all duration-300 text-sm flex items-center justify-center gap-2"
                  >
                    <Eye className="w-4 h-4" /> View Details
                  </button>
                </DashboardCard>
              )
            )}
          </div>

          {/* Pagination Controls */}
          {pagination.totalPages > 1 && (
            <div className="mt-10 flex justify-center items-center space-x-4">
              <button
                onClick={() => handlePageChange(pagination.pageNumber - 1)}
                disabled={pagination.pageNumber === 1}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:text-slate-600 text-slate-100 font-semibold rounded-lg border border-slate-600 transition-all duration-300 text-sm"
              >
                Previous
              </button>
              <span className="text-slate-300 text-sm">
                Page {pagination.pageNumber} of {pagination.totalPages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.pageNumber + 1)}
                disabled={pagination.pageNumber === pagination.totalPages}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:text-slate-600 text-slate-100 font-semibold rounded-lg border border-slate-600 transition-all duration-300 text-sm"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default InterviewHistoryPage;
